import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  Heading,
  HStack,
  Image,
  Pressable,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Link as RRDLink, Outlet, useLocation } from "react-router-dom";
import styles from "./styles.module.css";
import logo from "../../assets/images/logo_sidebar.png";
import colors from "../../constants/colors";
import routes from "../../routes";
import layout from "../../assets/icons/layout.svg";
import plusSquare from "../../assets/icons/plus-square.svg";
import dollarSign from "../../assets/icons/dollar-sign.svg";
import navigation from "../../assets/icons/navigation.svg";
import minusSquare from "../../assets/icons/minus-square.svg";
import clipboard from "../../assets/icons/clipboard.svg";
import barChart from "../../assets/icons/bar-chart.svg";
import usersThree from "../../assets/icons/users-three.svg";
import profileCircled from "../../assets/icons/profile-circled.svg";
import panelBottom from "../../assets/icons/panel-bottom.svg";
import { getColorFormat } from "../../utils";
import constants from "../../constants";
import classNames from "classnames";
import Modal from "../../components/modal";

function Link(props) {
  const { icon, isContracted, label, to, ...rest } = props;
  const location = useLocation();

  const active = location.pathname.startsWith(to);

  return (
    <RRDLink to={to} {...rest}>
      <HStack
        paddingY={3}
        paddingLeft={33}
        space={3}
        backgroundColor={active && getColorFormat(colors.primary, 0.1)}
        borderRightColor={colors.primary}
        borderRightWidth={active ? 5 : 0}>
        <Image
          src={icon}
          alt="icon"
          width={6}
          height={6}
          tintColor={active ? colors.primary : colors.text}
        />
        {!isContracted && (
          <Heading
            fontSize="md"
            color={active ? colors.primary : colors.text}
            fontWeight={active ? 700 : "normal"}
            numberOfLines={1}>
            {label}
          </Heading>
        )}
      </HStack>
    </RRDLink>
  );
}

function VendorLayout() {
  const location = useLocation();
  const container = useRef();
  const { isOpen, onOpen, onClose } = useDisclose();
  const [isContracted, setIsContracted] = useState(
    Boolean(localStorage.getItem(constants.IS_SIDEBAR_CONTRACTED)),
  );

  useEffect(() => {
    localStorage.setItem(
      constants.IS_SIDEBAR_CONTRACTED,
      isContracted ? "1" : "",
    );
  }, [isContracted]);

  useEffect(() => {
    if (
      location.pathname.startsWith(routes.agencyExperienceCreation) &&
      window.innerWidth < 1500
    ) {
      setIsContracted(true);
    }
    if (container.current) {
      container.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const idCotizacion = useMemo(() => {
    const path = location.pathname.split("/");
    const index = path.findIndex((string) => string === "experience-creation");
    if (index >= 0) {
      return path.at(index + 1);
    }
    return null;
  }, [location]);

  return (
    <HStack flex={1} alignItems="unset">
      <div
        className={classNames(styles.sidebar, {
          [styles.sidebarContracted]: isContracted,
        })}>
        <VStack
          backgroundColor={colors.white}
          width="100%"
          height="100%"
          space={6}>
          <HStack
            backgroundColor={colors.third}
            paddingY={5}
            paddingX={isContracted ? 4 : 10}
            space={5}>
            <RRDLink
              to={routes.agencyDashboard}
              className={styles.link}
              onClick={(e) => {
                if (idCotizacion) {
                  e.preventDefault();
                  onOpen();
                }
              }}>
              <Image
                src={logo}
                alt="logo"
                maxWidth={85}
                width="100%"
                height={45}
              />
            </RRDLink>
            {!isContracted && (
              <Heading color={colors.white} fontSize="lg">
                Empresa
              </Heading>
            )}
          </HStack>
          <div className={styles.container}>
            <VStack space={1}>
              <Link
                icon={layout}
                isContracted={isContracted}
                label="Dashboard"
                to={routes.agencyDashboard}
              />
              <Link
                icon={plusSquare}
                isContracted={isContracted}
                label="Creación de experiencia"
                to={routes.agencyExperienceCreation}
              />
              <Link
                icon={dollarSign}
                isContracted={isContracted}
                label="Gestión de Pagos"
                to={routes.agencyPaymentManagement}
              />
              <Link
                icon={navigation}
                isContracted={isContracted}
                label="Viajes y pasajeros"
                to={routes.agencyTripsAndPassengers}
              />
              <Link
                icon={minusSquare}
                isContracted={isContracted}
                label="Boletos"
                to={routes.tickets}
                disabled
              />
              <Link
                icon={clipboard}
                isContracted={isContracted}
                label="Cotizaciones"
                to={routes.agencyQuotations}
              />
              <Link
                icon={barChart}
                isContracted={isContracted}
                label="Reportes"
                to={routes.reports}
                disabled
              />
              <Link
                icon={usersThree}
                isContracted={isContracted}
                label="Usuarios"
                to={routes.users}
                disabled
              />
              <Link
                icon={profileCircled}
                isContracted={isContracted}
                label="Mi perfil"
                to={routes.agencyProfile}
              />
            </VStack>
          </div>
          <Pressable
            onPress={() => setIsContracted(!isContracted)}
            marginBottom={6}>
            <HStack paddingY={3} paddingLeft={33} space={3}>
              <Image
                src={panelBottom}
                alt="panelBottom"
                width={6}
                height={6}
                tintColor={colors.text}
              />
              {!isContracted && (
                <Text color={colors.text} numberOfLines={1}>
                  Contraer menú
                </Text>
              )}
            </HStack>
          </Pressable>
        </VStack>
      </div>
      <VStack
        ref={container}
        flex={1}
        backgroundColor={colors.background}
        overflowY="auto"
        overflowX="hidden"
        paddingX={10}
        paddingBottom={20}>
        <Outlet />
      </VStack>
      <Modal maxWidth={700} isOpen={isOpen} onRequestClose={onClose}>
        <VStack paddingX={10} paddingY={8} space={5}>
          <Heading color={colors.secondary} fontSize="2xl">
            Estás a punto de salir de tu cotización
          </Heading>
          <Divider backgroundColor={colors.primary} />
          <Text>
            Al salir de tu cotizacion la información de tu viaje quedará
            quardada en el módulo de cotizaciones con el folio #{idCotizacion},
            tu cotización expirará una vez finalizada la fecha de inicio de tu
            viaje
          </Text>
          <HStack space={5}>
            <button className={styles.flex} onClick={onClose}>
              <Box
                backgroundColor={colors.primary}
                paddingY={3}
                paddingX={50}
                borderRadius="full">
                <Text color={colors.white} textAlign="center">
                  Regresar
                </Text>
              </Box>
            </button>
            <RRDLink className={styles.flex} to={routes.home} onClick={onClose}>
              <Box
                backgroundColor={colors.white}
                paddingY={3}
                paddingX={50}
                borderRadius="full">
                <Text color={colors.primary} textAlign="center">
                  Salir de la cotización
                </Text>
              </Box>
            </RRDLink>
          </HStack>
        </VStack>
      </Modal>
    </HStack>
  );
}

Link.propTypes = {
  icon: PropTypes.any.isRequired,
  isContracted: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
};

export default VendorLayout;
